import { useTheme } from '@mui/material/styles'
import { createFileRoute, getRouteApi } from '@tanstack/react-router'
import { circle, point } from '@turf/turf'
import { compact } from 'es-toolkit'
import { Layer, Source } from 'react-map-gl/mapbox'
import { Box } from '../../../components/Box.tsx'
import { Collapse, Paper } from '../../../components/generic.tsx'
import { Stack } from '../../../components/Stack.tsx'
import { assetQuery } from '../../../data/assets.ts'
import { deviceListQuery } from '../../../data/devices.ts'
import type { Location } from '../../../data/locations.tsx'
import { CustomLayers } from '../../../features/layers/customLayers.tsx'
import { LayerSettings } from '../../../features/layers/LayerSettings.tsx'
import { LocationGeometry } from '../../../features/locations/LocationGeometry.tsx'
import { LocationMarkers } from '../../../features/locations/LocationMarkers.tsx'
import { useFilteredLocations } from '../../../features/locations/locationUtils.ts'
import { MapBox } from '../../../features/map/Map.tsx'
import { MapControls } from '../../../features/map/MapControls.tsx'
import { AssetAlertCards, DeviceAlertCards } from '../../../features/panel/AlertsPanel.tsx'
import { useMapsglSync, useNowSync } from '../../../features/timeline/Timeline.tsx'
import { useMyLocation } from '../../../states.ts'

export const Route = createFileRoute('/_app/m/_mobile/map')({
	component: RouteComponent,
	loader: async ({ context: { impersonation, queryClient, sub } }) => {
		await Promise.all([
			queryClient.ensureQueryData(deviceListQuery(impersonation?.partyAccountId)),
			queryClient.ensureQueryData(assetQuery(impersonation, { enabled: sub.features.includes('protections') })),
		])
	},
})

const route = getRouteApi('/_app/m/_mobile/map')

function RouteComponent() {
	const { locationId } = route.useSearch({ select: ({ locationId }) => ({ locationId }) })
	const { locations } = useFilteredLocations()

	const location = locations?.find((a) => a.id === locationId)
	const navigate = route.useNavigate()
	const layerGroups = route.useSearch({ select: ({ layerGroups }) => layerGroups })
	const setIsFollowing = useMyLocation((s) => s.setIsFollowing)

	const selectLocation = async (loc: Location) => {
		setIsFollowing(false)
		if (location?.id === loc.id) {
			await navigate({ search: (prev) => ({ ...prev, locationId: undefined, coordinates: undefined }), hash: true })
		} else {
			await navigate({ search: (prev) => ({ ...prev, locationId: loc.id, coordinates: undefined }) })
		}
	}

	const onMapClick = async ({ lat, lon }: { lat: number; lon: number }) => {
		await navigate({
			search: (prev) => ({ ...prev, locationId: undefined, coordinates: { lat, lon }, panel: 'weather' }),
			hash: true,
		})
	}

	useMapsglSync()
	useNowSync()

	return (
		<MapBox
			selectedLocationId={locationId}
			logoPosition='top-left'
			attributionProps={{ position: 'top-right' }}
			onDragStart={() => setIsFollowing(false)}
		>
			<LocationGeometry locations={locations} />
			<LocationMarkers selectedLocations={compact([location])} onLocationSelect={selectLocation} onMapClick={onMapClick} />
			<OwnLocation />
			<CustomLayers />
			<Box flex full col justify='end' align='end' px={1} gap={1}>
				<LayerSettings config='CLOSED' setConfig={() => {}} sx={{ display: 'none' }} />
				<MapControls
					onSevereLayerToggle={async () => {
						if (layerGroups?.includes('severe')) {
							await navigate({ search: (prev) => ({ ...prev, layerGroups: undefined, layers: undefined }), hash: true })
						} else {
							await navigate({
								search: (prev) => ({
									...prev,
									layerGroups: ['severe'],
									layers: ['lightning-realtime', 'lightning-alert-rings', 'lightning-threatzone', 'hail-threats'],
								}),
								hash: true,
							})
						}
					}}
				/>
				<Collapse in={!!locationId} sx={{ w: '100%', zIndex: 1 }}>
					<Paper component={Stack} sx={{ mb: 1, overflow: 'auto' }}>
						{location && (
							<>
								{location.type === 'asset' && <AssetAlertCards asset={location} />}
								{location.type === 'device' && <DeviceAlertCards device={location} />}
							</>
						)}
					</Paper>
				</Collapse>
			</Box>
		</MapBox>
	)
}

const OwnLocation = () => {
	const { palette } = useTheme()
	const { ownLocation } = palette
	const myCoords = useMyLocation((s) => s.coords)

	if (!myCoords) return null

	const myPoint = point([myCoords.longitude, myCoords.latitude])
	const myAccuracy = circle(myPoint, myCoords.accuracy, { steps: 32, units: 'meters' })

	return (
		<>
			<Source id='my-point-source' type='geojson' data={myPoint}>
				<Layer
					id='my-point'
					type='circle'
					paint={{
						'circle-radius': 6,
						'circle-color': ownLocation.fill,
						'circle-stroke-width': 1,
						'circle-stroke-color': ownLocation.outline,
					}}
				/>
			</Source>
			<Source id='my-accuracy-source' type='geojson' data={myAccuracy}>
				<Layer
					id='my-accuracy'
					type='fill'
					paint={{
						'fill-color': ownLocation.accuracyFill,
						'fill-outline-color': ownLocation.accuracyOutline,
					}}
				/>
			</Source>
		</>
	)
}
